import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { serversApi, companiesApi } from '../api'
import { useServerStatusHub } from '../hooks/useSignalR'
import { useMonitoringCommand } from '../hooks/useMonitoringCommand'
import { useAuthStore } from '../store/authStore'
import { SCOPE_EDGE_LICENSES, SCOPE_EDGE_SERVERS } from '../config/apps'
import Toast from '../components/Toast'
import { Lamp } from '../components/Lamp'
import { LampBoard } from '../components/serverStatus'
import { relativeTime } from '../components/serverLamp'
import ServerCard, { type CardStatus } from '../components/ServerCard'
import type { EdgeServer, ServerLiveStatus } from '../types'


// 대시보드 연동 전(승인 대기·활성화 대기) 서버 — 눌러서 상세로 들어가 라이센스를 발급한다
function PendingServerRow({ server, onClick }: { server: EdgeServer; onClick: () => void }) {
  const pending = server.status === 'PendingApproval'
  return (
    <li>
      <button onClick={onClick}
        className="w-full flex items-center gap-4 px-4 py-3 text-left hover:bg-gray-50 transition-colors">
        <Lamp state="warn" />
        <div className="min-w-0 flex-1">
          <p className="font-medium text-gray-900 truncate">{server.name}</p>
          <p className="text-xs text-gray-500 font-mono truncate">{server.id}</p>
        </div>
        <p className="hidden md:block text-[13px] text-gray-500 max-w-sm">
          {pending ? '라이센스를 발급하면 대시보드에 연동됩니다.' : '라이센스를 발급했고 Edge 연결을 기다리는 중입니다.'}
        </p>
        <span className="shrink-0 text-xs font-medium text-gray-800">{pending ? '승인 대기' : '활성화 대기'}</span>
        <span className="shrink-0 w-20 text-right text-xs text-gray-500" title={new Date(server.createdAt).toLocaleString('ko-KR')}>
          {relativeTime(server.createdAt)}
        </span>
      </button>
    </li>
  )
}

// 연동 대기 서버 — 라이센스 발급 권한자만 본다 (발급할 수 있는 사람에게만 의미가 있다)
function PendingSection({ servers, onOpen }: { servers: EdgeServer[]; onOpen: (s: EdgeServer) => void }) {
  if (servers.length === 0) return null
  return (
    <section className="mb-8">
      <SectionTitle count={servers.length}>연동을 기다리는 서버</SectionTitle>
      <ul className="bg-surface rounded-xl border border-dashed border-gray-300 divide-y divide-gray-100 overflow-hidden">
        {servers.map(s => <PendingServerRow key={s.id} server={s} onClick={() => onOpen(s)} />)}
      </ul>
    </section>
  )
}

function PageTitle({ children }: { children: React.ReactNode }) {
  return <h2 className="text-xl font-semibold tracking-tight text-gray-900 mb-5">{children}</h2>
}

function SectionTitle({ count, children }: { count: number; children: React.ReactNode }) {
  return (
    <h3 className="text-sm font-semibold text-gray-800 mb-2.5">
      {children} <span className="tabular font-normal text-gray-500 ml-1">{count}</span>
    </h3>
  )
}

// 화면 폭에 맞춰 열 수를 늘린다 — 고정 브레이크포인트면 넓은 모니터에서 카드만 늘어난다
const GRID = 'grid grid-cols-[repeat(auto-fill,minmax(min(100%,300px),1fr))] gap-3'

function EmptyServers() {
  return (
    <p className="col-span-full bg-surface rounded-xl border border-gray-200 px-6 py-10 text-center text-sm text-gray-500">
      등록된 서버가 없습니다. LiveEdge 를 설치하고 등록 요청을 보내면 여기에 나타납니다.
    </p>
  )
}

function AdminDashboard() {
  const navigate = useNavigate()
  const [liveStatus, setLiveStatus] = useState<Record<string, ServerLiveStatus>>({})
  const [onlineOverrides, setOnlineOverrides] = useState<Record<string, boolean>>({})
  const [lastDataOverrides, setLastDataOverrides] = useState<Record<string, string>>({})

  const { data: overview = [] } = useQuery({
    queryKey: ['companies-overview'],
    queryFn: () => companiesApi.getOverview().then(r => r.data),
    refetchInterval: 15000,
  })

  // 목록/신규 서버 반영용으로 15초 폴링은 유지하고, CPU/MEM/마지막 데이터는 SignalR 로 실시간 갱신
  const { data: allServers = [] } = useQuery({
    queryKey: ['servers'],
    queryFn: () => serversApi.getAll().then(r => r.data),
    refetchInterval: 15000,
  })

  const onStatusUpdate = useCallback((serverId: string, status: unknown) => {
    setLiveStatus(prev => ({ ...prev, [serverId]: status as ServerLiveStatus }))
  }, [])

  const onOnlineChanged = useCallback((serverId: string, isOnline: boolean) => {
    setOnlineOverrides(prev => ({ ...prev, [serverId]: isOnline }))
  }, [])

  const onLastDataAt = useCallback((serverId: string, lastDataAt: string) => {
    setLastDataOverrides(prev => ({ ...prev, [serverId]: lastDataAt }))
  }, [])

  useServerStatusHub(onStatusUpdate, onOnlineChanged, undefined, onLastDataAt, true)

  const canLicenses = useAuthStore(s => s.can(SCOPE_EDGE_LICENSES))
  const { registered: servers, pending: pendingServers } = splitServers(allServers)

  const { pendingCommands, sendCommand, toast, setToast } = useMonitoringCommand(true)

  const totalCompanies = overview.length
  const totalUsers = overview.reduce((s, c) => s + c.userCount, 0)
  // 램프 보드는 실시간 온라인 변경을 반영한 목록으로
  const liveServers = servers.map(s => onlineOverrides[s.id] !== undefined ? { ...s, isOnline: onlineOverrides[s.id] } : s)

  return (
    <div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <PageTitle>전체 서버</PageTitle>
      <LampBoard servers={liveServers}
        aside={<>회사 <span className="tabular font-medium text-gray-800">{totalCompanies}</span>곳, 회원 <span className="tabular font-medium text-gray-800">{totalUsers}</span>명</>} />

      {canLicenses && <PendingSection servers={pendingServers} onOpen={s => navigate(`/servers/${s.id}`)} />}

      {/* Edge 서버 기준 전체 목록 */}
      <SectionTitle count={servers.length}>서버</SectionTitle>
      <div className={GRID}>
        {servers.map(server => {
          const resolved = onlineOverrides[server.id] !== undefined
            ? { ...server, isOnline: onlineOverrides[server.id] }
            : server
          const live = liveStatus[server.id]
          const status: CardStatus | undefined = live
            ? { cpuUsage: live.cpuUsage, memoryUsage: live.memoryUsage, diskUsage: live.diskUsage }
            : serverFlatStatus(server)
          const lastMachineLogAt = lastDataOverrides[server.id] ?? server.lastMachineLogAt
          return (
            <ServerCard
              key={server.id}
              server={{ ...resolved, lastMachineLogAt }}
              status={status}
              showCompany
              onClick={() => navigate(`/servers/${server.id}`)}
              command={{ pendingType: pendingCommands[server.id], onCommand: sendCommand }}
            />
          )
        })}
        {servers.length === 0 && <EmptyServers />}
      </div>
    </div>
  )
}

// 등록 완료 서버만 일반 카드·집계 대상. 승인 대기·활성화 대기는 연동 대기 섹션으로 따로 보여준다
function splitServers(servers: EdgeServer[]) {
  return {
    registered: servers.filter(s => s.status === 'Registered'),
    pending: servers.filter(s => s.status !== 'Registered'),
  }
}

function serverFlatStatus(server: EdgeServer): CardStatus | undefined {
  return server.cpuUsage != null
    ? { cpuUsage: server.cpuUsage, memoryUsage: server.memoryUsage!, diskUsage: server.diskUsage! }
    : undefined
}

function UserDashboard() {
  const navigate = useNavigate()
  const { companyId } = useAuthStore()
  const [liveStatus, setLiveStatus] = useState<Record<string, ServerLiveStatus>>({})
  const [onlineOverrides, setOnlineOverrides] = useState<Record<string, boolean>>({})
  const [lastDataOverrides, setLastDataOverrides] = useState<Record<string, string>>({})

  const { data: allServers = [] } = useQuery({
    queryKey: ['servers'],
    queryFn: () => serversApi.getAll().then(r => r.data),
  })

  const onStatusUpdate = useCallback((serverId: string, status: unknown) => {
    setLiveStatus(prev => ({ ...prev, [serverId]: status as ServerLiveStatus }))
  }, [])

  const onOnlineChanged = useCallback((serverId: string, isOnline: boolean) => {
    setOnlineOverrides(prev => ({ ...prev, [serverId]: isOnline }))
  }, [])

  const onLastDataAt = useCallback((serverId: string, lastDataAt: string) => {
    setLastDataOverrides(prev => ({ ...prev, [serverId]: lastDataAt }))
  }, [])

  useServerStatusHub(onStatusUpdate, onOnlineChanged, companyId, onLastDataAt)

  const canLicenses = useAuthStore(s => s.can(SCOPE_EDGE_LICENSES))
  const { registered: servers, pending: pendingServers } = splitServers(allServers)

  const liveServers = servers.map(s => onlineOverrides[s.id] !== undefined ? { ...s, isOnline: onlineOverrides[s.id] } : s)

  return (
    <div>
      <PageTitle>대시보드</PageTitle>
      <LampBoard servers={liveServers} />
      {canLicenses && <PendingSection servers={pendingServers} onOpen={s => navigate(`/servers/${s.id}`)} />}
      <SectionTitle count={servers.length}>서버</SectionTitle>
      <div className={GRID}>
        {servers.map(server => {
          const resolved = onlineOverrides[server.id] !== undefined
            ? { ...server, isOnline: onlineOverrides[server.id] }
            : server
          const live = liveStatus[server.id]
          const status: CardStatus | undefined = live
            ? { cpuUsage: live.cpuUsage, memoryUsage: live.memoryUsage, diskUsage: live.diskUsage }
            : serverFlatStatus(server)
          const lastMachineLogAt = lastDataOverrides[server.id] ?? server.lastMachineLogAt
          return (
            <ServerCard
              key={server.id}
              server={{ ...resolved, lastMachineLogAt }}
              status={status}
              onClick={() => navigate(`/servers/${resolved.id}`)}
            />
          )
        })}
        {servers.length === 0 && <EmptyServers />}
      </div>
    </div>
  )
}

export default function Dashboard() {
  // 전 회사 서버를 볼 수 있으면(Admin 또는 edge:servers) 관리자 대시보드, 아니면 자기 회사 대시보드
  const { can } = useAuthStore()
  return can(SCOPE_EDGE_SERVERS) ? <AdminDashboard /> : <UserDashboard />
}
